import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { businessProfileApi } from '../api';
import { isRetryableError } from '../api/errors';
import {
    BorrowerEntity,
    BusinessProfile,
    BusinessReference,
    KeyEmployee,
    Officer,
    OrgChartNode,
    Owner,
    OwnerPercentage,
    WizardState,
} from '../types';

export enum BusinessProfileQueryKeys {
    WizardState = 'bp.wizardState',
    BusinessProfile = 'bp.businessProfile',
    BorrowerEntities = 'bp.borrowerEntities',
    BorrowerEntity = 'bp.borrowerEntity',
    Owners = 'bp.owners',
    Officers = 'bp.officers',
    KeyEmployees = 'bp.keyEmployees',
    References = 'bp.references',
    OrgChart = 'bp.orgChart',
}

export const BP_MUTATION_SCOPE = 'bp.mutation';
export const BP_MODAL_MUTATION_SCOPE = 'bp.modalMutation';
const ALL_QUERY_KEYS: ReadonlySet<string> = new Set(Object.values(BusinessProfileQueryKeys));

export const isBusinessProfileQueryKey = (queryKey: readonly unknown[]): boolean =>
    typeof queryKey[0] === 'string' && ALL_QUERY_KEYS.has(queryKey[0]);

export const wizardStateKey = () => [BusinessProfileQueryKeys.WizardState] as const;
export const businessProfileKey = () => [BusinessProfileQueryKeys.BusinessProfile] as const;
export const borrowerEntitiesKey = (businessProfileId: number) =>
    [BusinessProfileQueryKeys.BorrowerEntities, businessProfileId] as const;
export const borrowerEntityKey = (entityId: number) =>
    [BusinessProfileQueryKeys.BorrowerEntity, entityId] as const;
export const ownersKey = (entityId: number) => [BusinessProfileQueryKeys.Owners, entityId] as const;
export const officersKey = (entityId: number) => [BusinessProfileQueryKeys.Officers, entityId] as const;
export const keyEmployeesKey = (entityId: number) =>
    [BusinessProfileQueryKeys.KeyEmployees, entityId] as const;
export const referencesKey = (businessProfileId: number) =>
    [BusinessProfileQueryKeys.References, businessProfileId] as const;
export const orgChartKey = (businessProfileId: number) =>
    [BusinessProfileQueryKeys.OrgChart, businessProfileId] as const;

const wizardQueryOptions = {
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount: number, error: unknown) => isRetryableError(error) && failureCount < 2,
} as const;

function upsertById<T extends { id: number }>(list: T[] | undefined, item: T): T[] {
    const current = list ?? [];
    const index = current.findIndex((existing) => existing.id === item.id);
    if (index < 0) {
        return [...current, item];
    }
    const next = [...current];
    next[index] = item;
    return next;
}

const removeById = <T extends { id: number }>(list: T[] | undefined, id: number): T[] =>
    (list ?? []).filter((existing) => existing.id !== id);

const markStale = (queryClient: QueryClient, queryKey: readonly unknown[]) =>
    queryClient.invalidateQueries({ queryKey, refetchType: 'none' });

// ─── Wizard state ────────────────────────────────────────────────────────────

const fetchWizardState = (): Promise<WizardState> => businessProfileApi.getWizardState();

export function useWizardStateQuery() {
    return useQuery({
        queryKey: wizardStateKey(),
        queryFn: fetchWizardState,
        ...wizardQueryOptions,
    });
}

export function useSaveWizardState() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MUTATION_SCOPE, 'saveWizardState'],
        mutationFn: async (changes: Partial<WizardState>) => {
            const current =
                queryClient.getQueryData<WizardState>(wizardStateKey()) ??
                (await queryClient.fetchQuery({ queryKey: wizardStateKey(), queryFn: fetchWizardState }));

            return businessProfileApi.saveWizardState({ ...current, ...changes });
        },
        onSuccess: (saved) => queryClient.setQueryData(wizardStateKey(), saved),
    });
}

// ─── Step 1: Business Profile ────────────────────────────────────────────────

export function useBusinessProfileQuery() {
    return useQuery({
        queryKey: businessProfileKey(),
        queryFn: (): Promise<BusinessProfile | null> => businessProfileApi.getBusinessProfile(),
        ...wizardQueryOptions,
    });
}

export function useSaveBusinessProfile() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MUTATION_SCOPE, 'saveBusinessProfile'],
        mutationFn: (profile: BusinessProfile) => businessProfileApi.saveBusinessProfile(profile),
        onSuccess: (saved) => {
            queryClient.setQueryData(businessProfileKey(), saved);
            // Creating the profile is what assigns the wizard its businessProfileId
            queryClient.setQueryData<WizardState>(wizardStateKey(), (prior) =>
                prior ? { ...prior, businessProfileId: saved.id } : prior,
            );
            markStale(queryClient, orgChartKey(saved.id));
        },
    });
}

// ─── Step 2: Borrower Entities ───────────────────────────────────────────────

export function useBorrowerEntitiesQuery(businessProfileId: number | null | undefined) {
    return useQuery({
        queryKey: borrowerEntitiesKey(businessProfileId ?? 0),
        queryFn: (): Promise<BorrowerEntity[]> => businessProfileApi.getBorrowerEntities(businessProfileId!),
        enabled: !!businessProfileId,
        ...wizardQueryOptions,
    });
}

export function useBorrowerEntityQuery(entityId: number | null | undefined) {
    return useQuery({
        queryKey: borrowerEntityKey(entityId ?? 0),
        queryFn: (): Promise<BorrowerEntity> => businessProfileApi.getBorrowerEntity(entityId!),
        enabled: !!entityId,
        ...wizardQueryOptions,
    });
}

export function useSaveBorrowerEntity() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MUTATION_SCOPE, 'saveBorrowerEntity'],
        mutationFn: (entity: BorrowerEntity) => businessProfileApi.saveBorrowerEntity(entity),
        onSuccess: (saved) => {
            queryClient.setQueryData(borrowerEntityKey(saved.id), saved);
            queryClient.setQueryData<BorrowerEntity[]>(borrowerEntitiesKey(saved.businessProfileId), (prior) =>
                upsertById(prior, saved),
            );
            queryClient.setQueryData<WizardState>(wizardStateKey(), (prior) =>
                prior ? { ...prior, activeEntityId: saved.id } : prior,
            );
            markStale(queryClient, orgChartKey(saved.businessProfileId));
        },
    });
}

// ─── Step 3: Owners ──────────────────────────────────────────────────────────

export function useOwnersQuery(entityId: number | null | undefined) {
    return useQuery({
        queryKey: ownersKey(entityId ?? 0),
        queryFn: (): Promise<Owner[]> => businessProfileApi.getOwners(entityId!),
        enabled: !!entityId,
        ...wizardQueryOptions,
    });
}

export function useSaveOwner(businessProfileId: number | null | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MODAL_MUTATION_SCOPE, 'saveOwner'],
        mutationFn: (owner: Owner) => businessProfileApi.saveOwner(owner),
        onSuccess: (saved) => {
            queryClient.setQueryData<Owner[]>(ownersKey(saved.ownedEntityId), (prior) =>
                upsertById(prior, saved),
            );
            if (businessProfileId) {
                markStale(queryClient, orgChartKey(businessProfileId));
            }
        },
    });
}

export function useDeleteOwner(entityId: number, businessProfileId: number | null | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MUTATION_SCOPE, 'deleteOwner'],
        mutationFn: (ownerId: number) => businessProfileApi.deleteOwner(ownerId),
        onSuccess: (_, ownerId) => {
            queryClient.setQueryData<Owner[]>(ownersKey(entityId), (prior) => removeById(prior, ownerId));
            if (businessProfileId) {
                markStale(queryClient, orgChartKey(businessProfileId));
            }
        },
    });
}

interface SaveOwnerPercentagesArgs {
    entityId: number;
    percentages: OwnerPercentage[];
}

export function useSaveOwnerPercentages(businessProfileId: number | null | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MUTATION_SCOPE, 'saveOwnerPercentages'],
        mutationFn: ({ entityId, percentages }: SaveOwnerPercentagesArgs) =>
            businessProfileApi.saveOwnerPercentages(entityId, percentages),
        onSuccess: (saved, { entityId }) => {
            queryClient.setQueryData<Owner[]>(ownersKey(entityId), saved);
            if (businessProfileId) {
                markStale(queryClient, orgChartKey(businessProfileId));
            }
        },
    });
}

// ─── Step 4: Officers ────────────────────────────────────────────────────────

export function useOfficersQuery(entityId: number | null | undefined) {
    return useQuery({
        queryKey: officersKey(entityId ?? 0),
        queryFn: (): Promise<Officer[]> => businessProfileApi.getOfficers(entityId!),
        enabled: !!entityId,
        ...wizardQueryOptions,
    });
}

export function useSaveOfficer() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MODAL_MUTATION_SCOPE, 'saveOfficer'],
        mutationFn: (officer: Officer) => businessProfileApi.saveOfficer(officer),
        onSuccess: (saved) =>
            queryClient.setQueryData<Officer[]>(officersKey(saved.entityId), (prior) =>
                upsertById(prior, saved),
            ),
    });
}

export function useDeleteOfficer(entityId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MUTATION_SCOPE, 'deleteOfficer'],
        mutationFn: (officerId: number) => businessProfileApi.deleteOfficer(officerId),
        onSuccess: (_, officerId) =>
            queryClient.setQueryData<Officer[]>(officersKey(entityId), (prior) =>
                removeById(prior, officerId),
            ),
    });
}

// ─── Step 5: Key Employees ───────────────────────────────────────────────────

export function useKeyEmployeesQuery(entityId: number | null | undefined) {
    return useQuery({
        queryKey: keyEmployeesKey(entityId ?? 0),
        queryFn: (): Promise<KeyEmployee[]> => businessProfileApi.getKeyEmployees(entityId!),
        enabled: !!entityId,
        ...wizardQueryOptions,
    });
}

export function useSaveKeyEmployee() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MODAL_MUTATION_SCOPE, 'saveKeyEmployee'],
        mutationFn: (employee: KeyEmployee) => businessProfileApi.saveKeyEmployee(employee),
        onSuccess: (saved) =>
            queryClient.setQueryData<KeyEmployee[]>(keyEmployeesKey(saved.entityId), (prior) =>
                upsertById(prior, saved),
            ),
    });
}

export function useDeleteKeyEmployee(entityId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MUTATION_SCOPE, 'deleteKeyEmployee'],
        mutationFn: (employeeId: number) => businessProfileApi.deleteKeyEmployee(employeeId),
        onSuccess: (_, employeeId) =>
            queryClient.setQueryData<KeyEmployee[]>(keyEmployeesKey(entityId), (prior) =>
                removeById(prior, employeeId),
            ),
    });
}

// ─── Step 6: References ──────────────────────────────────────────────────────

export function useReferencesQuery(businessProfileId: number | null | undefined) {
    return useQuery({
        queryKey: referencesKey(businessProfileId ?? 0),
        queryFn: (): Promise<BusinessReference[]> => businessProfileApi.getReferences(businessProfileId!),
        enabled: !!businessProfileId,
        ...wizardQueryOptions,
    });
}

export function useSaveReference() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MODAL_MUTATION_SCOPE, 'saveReference'],
        mutationFn: (reference: BusinessReference) => businessProfileApi.saveReference(reference),
        onSuccess: (saved) =>
            queryClient.setQueryData<BusinessReference[]>(referencesKey(saved.businessProfileId), (prior) =>
                upsertById(prior, saved),
            ),
    });
}

export function useDeleteReference(businessProfileId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [BP_MUTATION_SCOPE, 'deleteReference'],
        mutationFn: (referenceId: number) => businessProfileApi.deleteReference(referenceId),
        onSuccess: (_, referenceId) =>
            queryClient.setQueryData<BusinessReference[]>(referencesKey(businessProfileId), (prior) =>
                removeById(prior, referenceId),
            ),
    });
}

// ─── Step 7: Org chart ───────────────────────────────────────────────────────

export function useOrgChartQuery(businessProfileId: number | null | undefined) {
    return useQuery({
        queryKey: orgChartKey(businessProfileId ?? 0),
        queryFn: (): Promise<OrgChartNode | null> => businessProfileApi.getOrgChart(businessProfileId!),
        enabled: !!businessProfileId,
        ...wizardQueryOptions,
    });
}

// ─── Completion ──────────────────────────────────────────────────────────────

// Closes the wizard session, which makes the company and entities it created visible to the
// rest of the system. Nothing the wizard creates reaches watchlists, lending limits or
// application matching until this is called.
export function useCompleteWizard() {
    return useMutation({
        mutationKey: [BP_MUTATION_SCOPE, 'complete'],
        mutationFn: () => businessProfileApi.complete(),
    });
}
